import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import moment from "moment";
import Seo from "../../components/Seo";
import { Avatar, EmptyState, Modal, PageHeader, Spinner } from "../../components/lawyer/LawyerUi";
import { apiErrorMessage, lawyerService } from "../../services/lawyerApi";

const placedAt = (call) => moment(call.started_at || call.created_at);

const planLabel = (call) =>
  [call.category, call.duration_minutes ? `${call.duration_minutes} Min` : null].filter(Boolean).join(" ");

const StatusPill = ({ status }) => (
  <span className={`lwp-status lwp-status-${(status || "").toLowerCase()}`}>
    {status ? status.charAt(0).toUpperCase() + status.slice(1) : "—"}
  </span>
);

const formatMoney = (amount, currency) => `${(Number(amount) || 0).toFixed(2)} ${currency || "USD"}`;

const CallDetails = ({ call, onClose }) => {
  const when = placedAt(call);
  const rows = [
    ["Client Name", call.user?.name],
    ["Category", call.category],
    ["Jurisdiction", call.jurisdiction],
    ["Plan", call.plan],
    ["Extended Plan", call.is_extended && call.extended_plan ? (
      <>
        {call.extended_plan} <span className="lwp-extended-badge">Extended during call</span>
      </>
    ) : null],
    ["Duration", call.talk_time],
    ["Date & Time", when.isValid() ? when.format("D MMM, YYYY, hh:mm A") : null],
    ["Net Earning", formatMoney(call.net_earning, call.currency)],
    ["Description", call.description],
  ].filter(([, v]) => v !== null && v !== undefined && v !== "");

  return (
    <Modal open onClose={onClose} className="lwp-call-modal">
      <div className="lwp-call-head">
        <Avatar src={call.user?.picture} alt={call.user?.name} size={72} />
        <div className="lwp-call-head-text">
          <h2>{call.user?.name || "Client"}</h2>
          <span>{call.talk_time}</span>
          {when.isValid() && (
            <span className="lwp-meta">
              <i className="bi bi-calendar3" /> {when.format("D MMM, YYYY, hh:mm A")}
            </span>
          )}
        </div>
        <button type="button" className="lwp-icon-btn lwp-modal-close" onClick={onClose} aria-label="Close">
          <i className="bi bi-x-lg" />
        </button>
      </div>

      <div className="lwp-call-stats">
        <div>
          <span className="lwp-call-stat-icon"><i className="bi bi-telephone-outbound" /></span>
          <span>
            <small>Call Duration</small>
            <strong>{call.talk_time || "00:00"}</strong>
          </span>
        </div>
        <div>
          <span className="lwp-call-stat-icon"><i className="bi bi-info-circle" /></span>
          <span>
            <small>Call Status</small>
            <strong className="lwp-muted-strong">
              {call.status ? call.status.charAt(0).toUpperCase() + call.status.slice(1) : "—"}
            </strong>
          </span>
        </div>
      </div>

      <h3 className="lwp-call-details-title">Call Details</h3>
      <dl className="lwp-detail-list">
        {rows.map(([label, value]) => (
          <div key={label}>
            <dt>{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
      {call.is_reported && (
        <p className="lwp-call-report">
          <i className="bi bi-flag-fill" /> Reported{call.report_description ? `: ${call.report_description}` : ""}
        </p>
      )}
    </Modal>
  );
};

const LawyerCallHistory = () => {
  const [calls, setCalls] = useState([]);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async (nextPage) => {
    try {
      const res = await lawyerService.getCallHistory(nextPage);
      if (!res?.data?.status) throw new Error(res?.data?.message);
      const data = res.data.data || {};
      setCalls((prev) => (nextPage === 1 ? data.calls || [] : [...prev, ...(data.calls || [])]));
      setPage(data.pagination?.current_page || nextPage);
      setLastPage(data.pagination?.last_page || 1);
    } catch (err) {
      toast.error(apiErrorMessage(err, err?.message || "Unable to load call history."));
    }
  }, []);

  useEffect(() => {
    load(1).finally(() => setLoading(false));
  }, [load]);

  const loadMore = async () => {
    setLoadingMore(true);
    await load(page + 1);
    setLoadingMore(false);
  };

  return (
    <div className="lwp-page">
      <Seo title="Call History | Legal Platform" noindex />
      <PageHeader title="Call History" />

      {loading ? (
        <Spinner />
      ) : calls.length === 0 ? (
        <EmptyState icon="bi-telephone" text="No calls yet. Your completed consultations will appear here." />
      ) : (
        <>
          <ul className="lwp-card-list">
            {calls.map((call) => {
              const when = placedAt(call);
              return (
                <li key={call.id}>
                  <button type="button" className="lwp-call-row" onClick={() => setSelected(call)}>
                    <Avatar src={call.user?.picture} alt={call.user?.name} size={64} />
                    <span className="lwp-call-row-body">
                      <strong>{call.user?.name || "Client"}</strong>
                      {planLabel(call) && <span>{planLabel(call)}</span>}
                      {when.isValid() && (
                        <span className="lwp-meta">
                          <i className="bi bi-calendar3" /> {when.format("D MMM YYYY hh:mm A")}
                        </span>
                      )}
                    </span>
                    <span className="lwp-call-row-side">
                      <StatusPill status={call.status} />
                      <span className="lwp-call-row-time">
                        {call.talk_time} <i className="bi bi-chevron-right" />
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
          {page < lastPage && (
            <button type="button" className="lwp-load-more" onClick={loadMore} disabled={loadingMore}>
              {loadingMore ? <span className="spinner-border spinner-border-sm" /> : "Load more"}
            </button>
          )}
        </>
      )}

      {selected && <CallDetails call={selected} onClose={() => setSelected(null)} />}
    </div>
  );
};

export default LawyerCallHistory;
